import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Timer, Target, TrendingUp, BarChart3, Star, Megaphone, Search, Zap } from "lucide-react";
import { Link } from "react-router-dom";

const rankingResults = [
  {
    trade: "Pool Builders",
    keyword: "pool resurfacing",
    timeframe: "4 months",
    beforeAvg: "20+",
    afterAvg: "1–3",
    improvement: "From invisible to top 3 across entire service area",
    image: "/images/heatmap-pool-builders.png",
  },
  {
    trade: "Painter",
    keyword: "painter",
    timeframe: "5 months",
    beforeAvg: "12–19",
    afterAvg: "1–2",
    improvement: "#1 rankings across entire metro area",
    image: "/images/heatmap-painter.png",
  },
  {
    trade: "Landscaper",
    keyword: "professional tree services",
    timeframe: "2 months",
    beforeAvg: "20+",
    afterAvg: "3–4",
    improvement: "Complete grid domination in just 8 weeks",
    image: "/images/heatmap-landscaper.png",
  },
  {
    trade: "Gutter Service",
    keyword: "gutter service",
    timeframe: "2 months",
    beforeAvg: "20+",
    afterAvg: "6–10",
    improvement: "From zero visibility to page 1 across the board",
    image: "/images/heatmap-gutter-service.png",
  },
];

const adResults = [
  {
    trade: "Renovations",
    location: "Sydney",
    platform: "Google Ads",
    metrics: [
      { label: "Monthly Leads", value: "20+", icon: TrendingUp },
      { label: "Cost Per Lead", value: "↓ 42%", icon: BarChart3 },
      { label: "Revenue Growth", value: "↑ 45%", icon: Star },
    ],
    summary:
      "Google Ads campaign restructured with hyper-local targeting and negative keyword refinement. Lead volume increased while cost per lead dropped by 42% within the first 3 months.",
  },
  {
    trade: "Electrician",
    location: "Brisbane",
    platform: "Google Ads",
    metrics: [
      { label: "Lead Quality", value: "↑ 60%", icon: TrendingUp },
      { label: "Tyre-Kickers", value: "↓ 70%", icon: BarChart3 },
      { label: "Conversion Rate", value: "3.8x", icon: Star },
    ],
    summary:
      "Refined targeting eliminated low-quality enquiries. Ad copy and landing pages optimised for high-intent searchers, tripling conversion rates from paid traffic.",
  },
  {
    trade: "Roofing",
    location: "Melbourne",
    platform: "Google Ads",
    metrics: [
      { label: "Booked Jobs", value: "↑ 55%", icon: TrendingUp },
      { label: "Ad Spend ROI", value: "8.2x", icon: BarChart3 },
      { label: "Quote Requests", value: "2x", icon: Star },
    ],
    summary:
      "Strategic campaign structure with service-specific ad groups and location targeting doubled quote requests and delivered 8.2x return on ad spend.",
  },
  {
    trade: "Painter",
    location: "Sydney",
    platform: "Facebook Ads",
    metrics: [
      { label: "Leads Per Month", value: "35+", icon: TrendingUp },
      { label: "Cost Per Lead", value: "$35", icon: BarChart3 },
      { label: "Booking Rate", value: "↑ 40%", icon: Star },
    ],
    summary:
      "Facebook lead generation campaign targeting homeowners in key suburbs. Before-and-after creative and instant form ads drove 35+ qualified leads per month at just $35 each.",
  },
  {
    trade: "Landscaping",
    location: "Gold Coast",
    platform: "Facebook Ads",
    metrics: [
      { label: "Enquiries", value: "3x", icon: TrendingUp },
      { label: "Ad Spend ROI", value: "6.5x", icon: BarChart3 },
      { label: "Reach", value: "↑ 280%", icon: Star },
    ],
    summary:
      "Targeted Facebook campaigns showcasing project galleries to local homeowners. Lookalike audiences and retargeting tripled enquiry volume with a 6.5x return on ad spend.",
  },
];

const automationResults = [
  {
    trade: "Plumber",
    location: "Perth",
    metrics: [
      { label: "Response Time", value: "<30s", icon: Zap },
      { label: "Missed Calls Recovered", value: "85%", icon: TrendingUp },
      { label: "After-Hours Leads", value: "↑ 3x", icon: Star },
    ],
    summary:
      "AI-powered instant responses and missed-call text-back system ensured no lead was lost — even outside business hours. After-hours enquiries tripled.",
  },
  {
    trade: "Builder",
    location: "Gold Coast",
    metrics: [
      { label: "Google Reviews", value: "5x", icon: Star },
      { label: "Review Response", value: "100%", icon: TrendingUp },
      { label: "Star Rating", value: "4.9★", icon: BarChart3 },
    ],
    summary:
      "Automated review collection system generated 5x more reviews in under two months. Every review received a personalised AI-generated response within minutes.",
  },
];

function SectionHeader({ badge, title, highlight, description }: { badge: string; title: string; highlight: string; description: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="max-w-3xl mx-auto text-center mb-8 sm:mb-12"
    >
      <div className="section-badge mb-3 sm:mb-4 text-xs">
        <span>{badge}</span>
      </div>
      <h2 className="heading-section text-foreground mb-3 sm:mb-4 text-balance">
        {title} <span className="text-primary">{highlight}</span>
      </h2>
      <p className="text-[0.9375rem] sm:text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto">
        {description}
      </p>
    </motion.div>
  );
}

export default function CaseStudies() {
  return (
    <>
      <Helmet>
        <title>Case Studies | Real Results for Trades & Service Businesses</title>
        <meta
          name="description"
          content="See real results from Australian trades and local service businesses using FullScale Marketing — more visibility, reviews, enquiries and booked work."
        />
        <link rel="canonical" href="https://fullscalemarketing.com.au/case-studies" />
        <meta property="og:title" content="Case Studies | Real Results for Trades & Service Businesses" />
        <meta property="og:description" content="Real results from Australian trades and local service businesses — more visibility, reviews and bookings." />
        <meta property="og:url" content="https://fullscalemarketing.com.au/case-studies" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-14 sm:pb-18 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-[hsl(100_25%_97%)] to-[hsl(98_30%_95%/0.6)]" />
        <div className="absolute inset-0 bg-gradient-hero-glow pointer-events-none" />
        <div className="absolute inset-0 grid-pattern opacity-30" />

        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="section-badge mb-4">
              <span className="text-xs">Case Studies</span>
            </div>
            <h1 className="text-[2rem] sm:text-[2.5rem] lg:text-[3.25rem] font-extrabold text-foreground leading-[1.1] mb-4 tracking-[-0.025em]">
              Real Results from{" "}
              <span className="text-primary">Real Trade & Service Businesses</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-[1.7] max-w-2xl mx-auto">
              See how trades and service businesses are dominating their local markets with our digital marketing system.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─── SECTION 1: Google Maps Rankings ─── */}
      <section className="section-padding bg-muted/30 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container-wide relative z-10">
          <SectionHeader
            badge="Google Maps Rankings"
            title="Local Ranking"
            highlight="Transformations"
            description="Before and after Google Maps heatmap results. Red = not ranking. Green = top positions. See the transformation our GBP optimisation delivers."
          />

          <div className="space-y-6 max-w-4xl mx-auto">
            {rankingResults.map((result, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="card-lift bg-card rounded-xl border border-border/60 shadow-card overflow-hidden"
              >
                <div className="p-5 sm:p-6 lg:p-8">
                  <div className="flex flex-col lg:flex-row gap-5 lg:gap-8">
                    {/* Heatmap Image */}
                    <div className="lg:w-1/2 shrink-0">
                      <div className="rounded-lg overflow-hidden border border-border/40 bg-muted aspect-[4/3] sm:aspect-[16/10]">
                        <img
                          src={result.image}
                          alt={`Google Maps ranking heatmap for ${result.trade} — ${result.keyword}`}
                          className="w-full h-full object-cover object-center"
                          loading="lazy"
                        />
                      </div>
                    </div>

                    {/* Details */}
                    <div className="flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-3.5 h-3.5 text-primary" />
                        <span className="text-xs font-bold text-primary uppercase tracking-wider">{result.trade}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Keyword: <span className="font-semibold text-foreground">"{result.keyword}"</span>
                      </p>

                      {/* Before / After */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-destructive/5 border border-destructive/15 rounded-lg p-3 sm:p-4 text-center">
                          <div className="text-[0.65rem] font-semibold text-destructive/70 uppercase tracking-wider mb-1">Before</div>
                          <div className="text-xl sm:text-2xl font-extrabold text-destructive">{result.beforeAvg}</div>
                          <div className="text-[0.65rem] text-muted-foreground">avg. position</div>
                        </div>
                        <div className="bg-primary/5 border border-primary/15 rounded-lg p-3 sm:p-4 text-center">
                          <div className="text-[0.65rem] font-semibold text-primary/70 uppercase tracking-wider mb-1">After</div>
                          <div className="text-xl sm:text-2xl font-extrabold text-primary">{result.afterAvg}</div>
                          <div className="text-[0.65rem] text-muted-foreground">avg. position</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-2">
                        <Timer className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="text-sm text-muted-foreground">Results in <strong className="text-foreground">{result.timeframe}</strong></span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Target className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                        <span className="text-sm text-muted-foreground leading-relaxed">{result.improvement}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-8">
            <Link
              to="/free-gbp-audit"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors shadow-md"
            >
              Get Your Free GBP Audit
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ─── SECTION 2: Google Ads ─── */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-25" />
        <div className="container-wide relative z-10">
          <SectionHeader
            badge="Paid Advertising"
            title="Google & Facebook Ads"
            highlight="Results"
            description="How our targeted ad campaigns across Google and Facebook drive qualified leads while reducing wasted spend for trade businesses."
          />

          <div className="space-y-5 max-w-4xl mx-auto">
            {adResults.map((result, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="card-lift bg-card rounded-xl p-6 sm:p-8 border border-border/60 shadow-card"
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Megaphone className="w-4 h-4 text-primary" />
                  <span className="text-xs font-bold text-primary uppercase tracking-wider">{result.trade}</span>
                  <span className="text-xs text-muted-foreground">— {result.location}</span>
                  <span className={`text-[0.65rem] font-semibold px-2 py-0.5 rounded-full ${result.platform === 'Facebook Ads' ? 'bg-blue-500/10 text-blue-600' : 'bg-primary/10 text-primary'}`}>
                    {result.platform}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-3 my-4">
                  {result.metrics.map((metric, j) => (
                    <div key={j} className="bg-muted rounded-lg p-2.5 sm:p-4 text-center">
                      <metric.icon className="w-4 h-4 text-primary mx-auto mb-1" />
                      <div className="text-base sm:text-xl font-bold text-foreground">{metric.value}</div>
                      <div className="text-xs text-muted-foreground">{metric.label}</div>
                    </div>
                  ))}
                </div>

                <p className="text-muted-foreground text-[0.9375rem] leading-[1.7]">{result.summary}</p>
              </motion.article>
            ))}
          </div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-8">
            <Link
              to="/services/lead-generation"
              className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:underline underline-offset-4"
            >
              Learn about our Lead Generation Ads
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ─── SECTION 3: Automation & Reviews ─── */}
      <section className="section-padding bg-muted/30 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container-wide relative z-10">
          <SectionHeader
            badge="Automation & Reviews"
            title="AI-Powered"
            highlight="Growth Systems"
            description="How automation tools and review management systems help tradies capture more leads and build trust."
          />

          <div className="space-y-5 max-w-4xl mx-auto">
            {automationResults.map((result, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="card-lift bg-card rounded-xl p-6 sm:p-8 border border-border/60 shadow-card"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-primary" />
                  <span className="text-xs font-bold text-primary uppercase tracking-wider">{result.trade}</span>
                  <span className="text-xs text-muted-foreground">— {result.location}</span>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-3 my-4">
                  {result.metrics.map((metric, j) => (
                    <div key={j} className="bg-muted rounded-lg p-2.5 sm:p-4 text-center">
                      <metric.icon className="w-4 h-4 text-primary mx-auto mb-1" />
                      <div className="text-base sm:text-xl font-bold text-foreground">{metric.value}</div>
                      <div className="text-xs text-muted-foreground">{metric.label}</div>
                    </div>
                  ))}
                </div>

                <p className="text-muted-foreground text-[0.9375rem] leading-[1.7]">{result.summary}</p>
              </motion.article>
            ))}
          </div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-8">
            <Link
              to="/services/automation"
              className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:underline underline-offset-4"
            >
              Explore our Automation & AI Tools
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 sm:py-20 bg-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground to-primary/20" />
        <div className="container-wide relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl font-extrabold text-background mb-3 tracking-tight">
              Want Results Like These?
            </h2>
            <p className="text-background/70 text-base sm:text-lg mb-6 max-w-xl mx-auto">
              Find out how your business stacks up with a free audit, or book a strategy call to get started.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/free-gbp-audit"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors shadow-md"
              >
                Free GBP Audit
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-background text-foreground px-6 py-3 rounded-lg font-semibold text-sm hover:bg-background/90 transition-colors shadow-md"
              >
                Book a Strategy Call
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
